import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Users, CheckCircle2, Activity,
  AlertCircle, UserX, XCircle, PlayCircle, Languages,
} from 'lucide-react';
import trainingApi from '../api';
import { labelFor, nativeLabelFor } from '../i18n/strings';

const relativeTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const MetricsHub = () => {
  const [matrix, setMatrix] = useState(null);
  const [allTrainings, setAllTrainings] = useState([]);
  const [recent, setRecent] = useState([]);
  const [languageStats, setLanguageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [m, all, r, langs] = await Promise.all([
          trainingApi.adminMetricsMatrix(),
          trainingApi.adminListAll(),
          trainingApi.adminRecentActivity(20),
          trainingApi.adminMetricsLanguages(),
        ]);
        if (!mounted) return;
        setMatrix(m);
        setAllTrainings(all);
        setRecent(r);
        setLanguageStats(langs);
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const stats = useMemo(() => {
    if (!matrix) return null;
    const { users, trainings, cells } = matrix;
    const cellsByUser = new Map();
    const attemptsByTraining = new Map();
    let totalAttempts = 0;
    let totalPassed = 0;
    for (const c of cells) {
      totalAttempts += c.attempts || 0;
      if (c.best_score !== null) totalPassed += 1;
      const ua = cellsByUser.get(c.user_id) || { attempts: 0, passed: 0 };
      ua.attempts += c.attempts || 0;
      if (c.best_score !== null) ua.passed += 1;
      cellsByUser.set(c.user_id, ua);
      const ta = attemptsByTraining.get(c.training_id) || 0;
      attemptsByTraining.set(c.training_id, ta + (c.attempts || 0));
    }
    const usersEngaged = [...cellsByUser.values()].filter((u) => u.attempts > 0).length;
    const usersPassedSomething = [...cellsByUser.values()].filter((u) => u.passed > 0).length;
    const usersNeverStarted = users.filter((u) => {
      const a = cellsByUser.get(u.id);
      return !a || a.attempts === 0;
    });
    const trainingsZeroAttempts = trainings.filter((t) => (attemptsByTraining.get(t.id) || 0) === 0);
    const totalPairs = users.length * trainings.length;
    const passRate = totalPairs > 0 ? Math.round((totalPassed / totalPairs) * 100) : 0;
    return {
      users,
      trainings,
      totalAttempts,
      totalPassed,
      passRate,
      usersEngaged,
      usersPassedSomething,
      usersNeverStarted,
      trainingsZeroAttempts,
    };
  }, [matrix]);

  const counts = useMemo(() => {
    const published = allTrainings.filter((t) => t.is_published).length;
    const draft = allTrainings.length - published;
    return { published, draft, total: allTrainings.length };
  }, [allTrainings]);

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (error) return <div className="p-8 text-rose-300">{error}</div>;
  if (!stats) return null;

  return (
    <div className="p-8 text-slate-100 space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">Metrics Hub</h1>
        <p className="text-slate-400 text-sm">Compliance overview across all trainings.</p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<BookOpen className="w-5 h-5" />}
          label="Trainings"
          value={counts.total}
          sub={`${counts.published} published · ${counts.draft} draft`}
          tint="text-amber-300"
        />
        <KpiCard
          icon={<Activity className="w-5 h-5" />}
          label="Total Attempts"
          value={stats.totalAttempts}
          sub={`${stats.totalPassed} passing scores`}
          tint="text-sky-300"
        />
        <KpiCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Overall Pass Rate"
          value={`${stats.passRate}%`}
          sub={`of ${stats.users.length} users × ${stats.trainings.length} trainings`}
          tint="text-emerald-300"
        />
        <KpiCard
          icon={<Users className="w-5 h-5" />}
          label="Engagement"
          value={`${stats.usersPassedSomething} / ${stats.users.length}`}
          sub="users have passed at least one"
          tint="text-violet-300"
        />
      </div>

      {/* Gap analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GapCard
          title="Trainings with zero attempts"
          icon={<AlertCircle className="w-4 h-4 text-amber-300" />}
          items={stats.trainingsZeroAttempts.map((t) => ({
            key: t.id,
            label: t.title,
            href: '/tiles/ksp_training/admin/by-training',
          }))}
          emptyMessage="Every published training has at least one attempt. 🎉"
        />
        <GapCard
          title="Users who haven't started"
          icon={<UserX className="w-4 h-4 text-amber-300" />}
          items={stats.usersNeverStarted.map((u) => ({
            key: u.id,
            label: u.full_name || u.username,
            sub: u.username,
            href: '/tiles/ksp_training/admin/by-user',
          }))}
          emptyMessage="Every user has started at least one training."
        />
      </div>

      {/* Which languages people actually train in */}
      {languageStats && <LanguageBreakdown stats={languageStats} />}

      {/* Recent activity */}
      <div className="rounded-lg bg-slate-800 border border-slate-700">
        <div className="px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold text-slate-200">Recent Activity</h2>
        </div>
        <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="p-5 text-sm text-slate-400">No activity yet.</div>
          ) : (
            recent.map((a) => <ActivityRow key={a.id} a={a} />)
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Completions per language, per training.
 *
 * This could not be read before: each language was its own course, so "how many people
 * trained in Spanish" meant eyeballing three separate rows. It is the number that
 * justifies the translation work, and it is how you spot a language version nobody is
 * using — or one that everybody is using.
 */
const LanguageBreakdown = ({ stats }) => {
  const byTraining = useMemo(() => {
    const map = new Map();
    for (const row of stats.by_training || []) {
      if (!map.has(row.training_id)) {
        map.set(row.training_id, { title: row.title, rows: [], totalUsers: 0 });
      }
      const entry = map.get(row.training_id);
      entry.rows.push(row);
      entry.totalUsers += Number(row.users) || 0;
    }
    return [...map.values()];
  }, [stats]);

  const profile = stats.workforce_by_profile_language || [];

  return (
    <div className="rounded-lg bg-slate-800 border border-slate-700">
      <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2 flex-wrap">
        <Languages className="w-4 h-4 text-violet-300" />
        <h2 className="text-sm font-semibold text-slate-200">Training by Language</h2>
        <span className="ml-auto text-xs text-slate-400">
          {profile.map((p) => `${p.language === 'unset' ? 'no preference' : labelFor(p.language)}: ${p.users}`).join(' · ')}
        </span>
      </div>
      <div className="divide-y divide-slate-700">
        {byTraining.length === 0 ? (
          <div className="p-5 text-sm text-slate-400">No attempts yet.</div>
        ) : byTraining.map((t) => (
          <div key={t.title} className="px-5 py-4">
            <div className="text-sm text-slate-200 mb-2">{t.title}</div>
            <div className="space-y-1.5">
              {t.rows.map((r) => {
                const share = t.totalUsers > 0 ? (Number(r.users) / t.totalUsers) * 100 : 0;
                return (
                  <div key={r.language} className="flex items-center gap-3 text-xs">
                    <span className="w-20 text-slate-300 flex-shrink-0">{nativeLabelFor(r.language)}</span>
                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500" style={{ width: `${share}%` }} />
                    </div>
                    <span className="w-40 text-right text-slate-400 flex-shrink-0">
                      {r.users_passed} passed · {r.users} took it
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, sub, tint }) => (
  <div className="rounded-lg bg-slate-800 border border-slate-700 p-4">
    <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${tint}`}>
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-100">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const GapCard = ({ title, icon, items, emptyMessage }) => (
  <div className="rounded-lg bg-slate-800 border border-slate-700">
    <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
      {icon}
      <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
      <span className="ml-auto text-xs text-slate-400">{items.length}</span>
    </div>
    <div className="max-h-72 overflow-y-auto">
      {items.length === 0 ? (
        <div className="p-5 text-sm text-slate-400">{emptyMessage}</div>
      ) : (
        <ul className="divide-y divide-slate-700">
          {items.map((it) => (
            <li key={it.key} className="px-5 py-3">
              <Link to={it.href} className="block hover:text-amber-300">
                <div className="text-sm text-slate-200">{it.label}</div>
                {it.sub && <div className="text-xs text-slate-400">{it.sub}</div>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const ActivityRow = ({ a }) => {
  const completed = !!a.completed_at;
  let verb;
  let Icon;
  let tint;
  if (!completed) {
    verb = 'started';
    Icon = PlayCircle;
    tint = 'text-sky-300';
  } else if (a.passed) {
    verb = 'passed';
    Icon = CheckCircle2;
    tint = 'text-emerald-300';
  } else {
    verb = 'failed';
    Icon = XCircle;
    tint = 'text-rose-300';
  }
  return (
    <div className="px-5 py-3 flex items-center gap-3">
      <Icon className={`w-4 h-4 flex-shrink-0 ${tint}`} />
      <div className="flex-1 min-w-0 text-sm text-slate-200 truncate">
        <span className="font-medium">{a.full_name || a.username}</span>{' '}
        <span className={tint}>{verb}</span>{' '}
        <span className="text-slate-300">{a.title}</span>
        {completed && a.score_pct !== null && (
          <span className="text-slate-400"> · {Number(a.score_pct).toFixed(0)}%</span>
        )}
        {a.language && a.language !== 'en' && (
          <span className="text-slate-500"> · {nativeLabelFor(a.language)}</span>
        )}
      </div>
      <div className="text-xs text-slate-400 whitespace-nowrap">
        {relativeTime(a.completed_at || a.started_at)}
      </div>
    </div>
  );
};

export default MetricsHub;
